import React, { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router";
// esri
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";
import Graphic from "@arcgis/core/Graphic";
import Collection from "esri/core/Collection";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
// components
import TextSymbol from "src/components/UI/Drawer-Widget/symbol";
import data from "src/config/search.json";
import TextPopup from "src/components/UI/Drawer-Widget/TextPopup";
import SketchTools from "src/components/UI/Drawer-Widget/Tools";
import BasePanel from "src/components/UI/Shared/BasePanel";
// assets
import styles from "src/components/UI/Drawer-Widget/assets/css/Draw.module.scss";
// data
import Store from "src/data/store";
// redux
import { useSelector, useDispatch } from "react-redux";
import { appActions } from "src/store/store";

const store = Store.getInstance();

interface AddedGraphic {
  type: string;
  id: string;
  text: string;
  size: number;
  color: string;
  isEditing: boolean;
  mapCoordinates: number[];
  coordinates: number[];
  [key: string]: any;
}
interface DrawingMeasurementObject {
  id: number;
  centerPoint1: number[];
  centerPoint2: number[];
  results: string[];
}
const pointSymbol: any = {
  type: "simple-marker",
  style: "circle",
  color: "#27dec4",
  size: "7px",
  outline: {
    color: "black",
    width: 1.5,
  },
};

const lineSymbol: any = {
  type: "simple-line",
  color: "#27dec4",
  width: "3px",
  style: "solid",
};

const polygonSymbol: any = {
  type: "simple-fill",
  color: "#27dec4",
  style: "none",
  outline: {
    color: "#27dec4",
    width: 2,
  },
};

const textSymbol: any = {
  type: "text",
  color: "black",
  haloColor: "black",
  haloSize: ".5px",
  text: "You are here",
  xoffset: -1,
  yoffset: 1.5,
  font: {
    size: 11,
    family: "Josefin Slab",
    weight: "bold",
  },
};
interface MeasurementObject {
  id: number;
  centerPoint1: number[];
  centerPoint2: number[];
  results: string[];
}

const Widget: React.FC<any> = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activePanel = useSelector((state: any) => state.app.activePanel);

  const [graphicsLayer, setGraphicsLayer] = React.useState<
    GraphicsLayer | undefined
  >(undefined);
  const [lastActiveTool, setLastActiveTool] = React.useState<any>("point");
  const [measurementPoints, setMeasurementPoints] = React.useState<any[]>([]);
  const [sketchViewModel, setSketchViewModel] =
    React.useState<SketchViewModel | null>(null);
  const [addedGraphics, setAddedGraphics] = React.useState<AddedGraphic[]>([]);
  const [open, setOpen] = React.useState(0);
  const [drawingSymbolMeasurements, setDrawingSymbolMeasurements] =
    React.useState<DrawingMeasurementObject[]>([]);
  const [layerIsHidden, setLayerIsHidden] = useState(false);

  const getHeight = () => {
    let element = document.getElementsByClassName("esri-view-root");
    const rect = element[0].getBoundingClientRect();
    return rect.y;
  };

  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason !== "backdropClick") {
      if (graphicsLayer) graphicsLayer.id = "general-drawing";
      dispatch(appActions.setActivePanel(undefined));
      dispatch(
        appActions.setSearchString({
          menu1: "",
          menu2: "",
          menu3: "",
          menu4: "",
          menu5: "",
          menu6: "",
        })
      );
      navigate("../..");
    }
  };
  React.useEffect(() => {
    if (graphicsLayer && activePanel !== "drawing") {
      graphicsLayer.id = "general-drawing";
      dispatch(
        appActions.setSearchString({
          menu1: "",
          menu2: "",
          menu3: "",
          menu4: "",
          menu5: "",
          menu6: "",
        })
      );
    }
  }, [activePanel]);
  React.useEffect(() => {
    if (measurementPoints.length >= 2) {
      const geometryType = measurementPoints[1].type;
      const lastPoint = measurementPoints[1].lastPoint;
      const id = measurementPoints[1].id;
      let paths = [measurementPoints[0], lastPoint];
      let line = new Polyline({
        paths: paths,
        spatialReference: store.MapView?.spatialReference,
      });

      let startPoint = new Point({
        x: measurementPoints[0][0],
        y: measurementPoints[0][1],
        spatialReference: store.MapView?.spatialReference,
      });
      let endPoint = new Point({
        x: lastPoint[0],
        y: lastPoint[1],
        spatialReference: store.MapView?.spatialReference,
      });
      let midpoint1 = [
        (measurementPoints[0][0] + lastPoint[0]) / 2,
        (measurementPoints[0][1] + lastPoint[1]) / 2,
      ];
      const distance = geometryEngine.geodesicLength(line, "miles");
      if (geometryType == "circle") {
        setDrawingSymbolMeasurements((prev: Array<MeasurementObject>) => {
          const measurementObjectIndex = prev.findIndex((obj) => obj.id === id);
          const newRec = {
            id: id,
            centerPoint1: midpoint1,
            centerPoint2: [],
            results: [distance.toFixed(1)],
          };
          const tmp = [...prev];
          if (measurementObjectIndex !== -1) {
            tmp[measurementObjectIndex] = newRec;
            tmp.push(newRec);
          } else {
            tmp.push(newRec);
          }
          return tmp;
        });
        graphicsLayer?.addMany([
          new Graphic({
            attributes: { id: id },
            geometry: line,
            symbol: lineSymbol,
          }),
          new Graphic({
            attributes: { id: id },
            geometry: startPoint,
            symbol: pointSymbol,
          }),
          new Graphic({
            attributes: { id: id },
            geometry: endPoint,
            symbol: pointSymbol,
          }),
        ]);
      } else if (geometryType == "polyline") {
        setDrawingSymbolMeasurements((prev: Array<MeasurementObject>) => {
          const measurementObjectIndex = prev.findIndex((obj) => obj.id === id);
          const newRec = {
            id: id,
            centerPoint1: midpoint1,
            centerPoint2: [],
            results: [distance.toFixed(1)],
          };
          const tmp = [...prev];
          if (measurementObjectIndex !== -1) {
            tmp[measurementObjectIndex] = newRec;
            tmp.push(newRec);
          } else {
            tmp.push(newRec);
          }
          return tmp;
        });
        graphicsLayer?.addMany([
          new Graphic({
            attributes: { id: id },
            geometry: startPoint,
            symbol: pointSymbol,
          }),
          new Graphic({
            attributes: { id: id },
            geometry: endPoint,
            symbol: pointSymbol,
          }),
        ]);
      } else {
        const lastPoint2 = measurementPoints[2].lastPoint2;
        let paths2 = [lastPoint, lastPoint2];
        let line2 = new Polyline({
          paths: paths2,
          spatialReference: store.MapView?.spatialReference,
        });

        let midpoint2 = [
          (lastPoint[0] + lastPoint2[0]) / 2,
          (lastPoint[1] + lastPoint2[1]) / 2,
        ];
        const distance2 = geometryEngine.geodesicLength(line2, "miles");
        setDrawingSymbolMeasurements((prev: Array<MeasurementObject>) => {
          const measurementObjectIndex = prev.findIndex((obj) => obj.id === id);
          const newRec = {
            id: id,
            centerPoint1: midpoint1,
            centerPoint2: midpoint2,
            results: [distance.toFixed(1), distance2.toFixed(1)],
          };
          const tmp = [...prev];
          if (measurementObjectIndex !== -1) {
            tmp[measurementObjectIndex] = newRec;
            tmp.push(newRec);
          } else {
            tmp.push(newRec);
          }
          return tmp;
        });
      }
    }
  }, [measurementPoints]);
  React.useEffect(() => {
    if (graphicsLayer) {
      _InitializeSketch();
    }
  }, [graphicsLayer]);
  React.useEffect(() => {
    if (store.MapView && open === 1) {
      store.MapView?.watch("extent", (evt) => {
        setAddedGraphics((prev) => {
          const tmpAddedGraphics = prev.map((x) => {
            const mapPoint = new Point({
              latitude: x.mapCoordinates[1],
              longitude: x.mapCoordinates[0],
            });
            const screenPoint = store.MapView?.toScreen(mapPoint);

            if (screenPoint) {
              return {
                ...x,
                coordinates: [screenPoint.x, screenPoint.y + getHeight()],
              };
            } else {
              return x;
            }
          });

          return tmpAddedGraphics;
        });
      });
      store.MapView?.when(() => {
        const tmpGraphicsLayer = store.createGraphicsLayer(
          data.services.General[0].title,
          "general-drawing-current"
        );
        setGraphicsLayer(tmpGraphicsLayer);
      });
    }
  }, [open]);
  React.useEffect(() => {
    if (activePanel === "drawing" && open === 0) {
      setOpen(1);
    }
  }, [activePanel]);
  let layers = store.MapView?.map.layers.toArray();
  useEffect(() => {
    if (layers) {
      const layer = layers.filter((x) => {
        return x.title === "Drawing";
      })[0] as GraphicsLayer;
      if (layer) setLayerIsHidden(!layer.visible);
      if (!layer && graphicsLayer) {
        const tmpGraphicsLayer = store.createGraphicsLayer(
          data.services.General[0].title,
          "general-drawing-current"
        );
        setGraphicsLayer(tmpGraphicsLayer);
        setAddedGraphics([]);
      }
    }
  }, [layers]);
  const _DeleteHandler = (id: string) => {
    setAddedGraphics((prev) => {
      const remainingGraphics = prev.filter((x) => x.id !== id);

      if (graphicsLayer) {
        const graphics = graphicsLayer.graphics as Collection<Graphic>;
        const toBeRemoved = graphics
          .toArray()
          .filter((x: Graphic) => String(x.attributes.id) === id);

        graphicsLayer.removeMany(toBeRemoved);
      }

      return remainingGraphics;
    });
  };

  const _UpdateHandler = (id: string, val: any, key: string) => {
    setAddedGraphics((prev) => {
      const targetItem = prev.filter((x) => x.id == id)[0];
      targetItem[key] = val;
      const graphics = graphicsLayer?.graphics as any;
      const targetGraphics = graphics.items.filter(
        (x: any) =>
          x.attributes &&
          !x.attributes.no_resize &&
          x.attributes.id &&
          x.attributes.id == id
      );
      targetGraphics?.forEach((graphic: Graphic) => {
        if (graphic.geometry.type === "point") {
          if (graphic.symbol.type == "text") {
            const tmpTextSymbol = { ...textSymbol };
            tmpTextSymbol.text = targetItem.text;
            tmpTextSymbol.color = targetItem.color;
            tmpTextSymbol.font.size = `${targetItem.size}px`;
            graphic.symbol = tmpTextSymbol;
          } else {
            const tmpPointSymbol = { ...pointSymbol };
            tmpPointSymbol.size = `${
              targetGraphics.length > 1 ? 7 : targetItem.size
            }px`;
            tmpPointSymbol.color = targetItem.color;
            graphic.symbol = tmpPointSymbol;
          }
        } else if (graphic.geometry.type === "polyline") {
          const tmpLineSymbol = { ...lineSymbol };
          tmpLineSymbol.width = `${targetItem.size}px`;
          tmpLineSymbol.color = targetItem.color;
          graphic.symbol = tmpLineSymbol;
        } else if (
          ["polygon", "extent", "rectangle", "circle"].includes(
            graphic.geometry.type
          )
        ) {
          const tmpPolygonSymbol = { ...polygonSymbol };
          tmpPolygonSymbol.outline.width = `${targetItem.size}px`;
          tmpPolygonSymbol.outline.color = targetItem.color;
          graphic.symbol = tmpPolygonSymbol;
        }
      });

      const remainingGraphics = prev.filter((x) => x.id != id);
      return remainingGraphics.concat(targetItem);
    });
  };
  const _SizeChangeHandler = (id: string, pValue: number) => {
    _UpdateHandler(id, pValue, "size");
  };
  const _TxtChangeHandler = (id: string, pValue: string) => {
    _UpdateHandler(id, pValue, "text");
  };
  const _ColorChangeHandler = (id: string, pValue: string) => {
    _UpdateHandler(id, pValue, "color");
  };
  const _SetEditHandler = (id: string, pValue: boolean) => {
    _UpdateHandler(id, pValue, "isEditing");
    if (sketchViewModel) {
      if (pValue) {
        if (lastActiveTool === "text") {
          sketchViewModel.pointSymbol = textSymbol;
          sketchViewModel.create("point");
        } else if (
          [
            "point",
            "multipoint",
            "polyline",
            "polygon",
            "rectangle",
            "circle",
          ].includes(lastActiveTool)
        ) {
          sketchViewModel.pointSymbol = pointSymbol;
          if (activePanel === "drawing") sketchViewModel.create(lastActiveTool);
        }
      }
    }
  };

  useEffect(() => {
    if (sketchViewModel) {
      sketchViewModel.on("create", function (event: any) {
        if (event.state === "start") {
          if (
            event.tool === "circle" ||
            event.tool === "polyline" ||
            event.tool === "rectangle"
          ) {
            const startPoint: any[] = event.toolEventInfo.added[0];
            setMeasurementPoints([[...startPoint]]);
          }
        }

        if (event.state === "complete") {
          const id: number = Math.random() * (1 - 1000);
          let screenPoint: any;
          let mapPoint: any = _GetStartPoint(
            event.graphic.geometry,
            event.tool
          );
          screenPoint = store.MapView?.toScreen(mapPoint);
          if (event.tool === "point") {
            // mapPoint = event.graphic.geometry;
          } else if (event.tool === "polyline") {
            const pathPoints = event.graphic.geometry.paths[0];
            const lastPoint = pathPoints[pathPoints.length - 1];
            mapPoint = new Point({
              x: lastPoint[0],
              y: lastPoint[1],
              spatialReference: event.graphic.geometry.spatialReference,
            });
          } else if (
            ["polygon", "rectangle", "circle"].indexOf(event.tool) != -1
          ) {
            const pathPoints = event.graphic.geometry.rings[0];
            const lastPoint = pathPoints[pathPoints.length - 3];
            mapPoint = new Point({
              x: lastPoint[0],
              y: lastPoint[1],
              spatialReference: event.graphic.geometry.spatialReference,
            });
          }
          const graphics = graphicsLayer?.graphics as any;
          const addedGraphics = graphics.items; // Assuming it's a Collection<Graphic>
          graphics.items[addedGraphics.length - 1].attributes = { id: id };
          setAddedGraphics((prev) => {
            const tmpAddedGraphics = [...prev];
            tmpAddedGraphics.push({
              id: `${id}`,
              text: "",
              isEditing: true,
              type: event.graphic.symbol.type == "text" ? "text" : event.tool,
              color: "#27dec4",
              size:
                event.tool == "point"
                  ? event.graphic.symbol.type == "text"
                    ? 11
                    : 7
                  : 2,
              coordinates: [screenPoint.x, screenPoint.y + getHeight()],
              mapCoordinates: [mapPoint.longitude, mapPoint.latitude],
            });
            return tmpAddedGraphics;
          });

          if (event.tool === "circle") {
            const pathPoints = event.graphic.geometry.rings[0];
            const lastPoint = pathPoints[pathPoints.length - 2];
            setMeasurementPoints((prev) => {
              const tmpPrev = [...prev];
              tmpPrev.push({ type: event.tool, lastPoint: lastPoint, id: id });
              return tmpPrev;
            });
          }
          if (event.tool === "polyline") {
            const pathPoints = event.graphic.geometry.paths[0];
            const lastPoint = pathPoints[pathPoints.length - 1];
            setMeasurementPoints((prev) => {
              const tmpPrev = [...prev];
              tmpPrev.push({ type: event.tool, lastPoint: lastPoint, id: id });
              return tmpPrev;
            });
          }
          if (event.tool === "rectangle") {
            const pathPoints = event.graphic.geometry.rings[0];
            const lastPoint = pathPoints[1];
            const lastPoint2 = pathPoints[2];
            setMeasurementPoints((prev) => {
              const tmpPrev = [...prev];
              tmpPrev.push({ type: event.tool, lastPoint: lastPoint, id: id });
              tmpPrev.push({
                type: event.tool,
                lastPoint2: lastPoint2,
                id: id,
              });
              return tmpPrev;
            });
          }
        }
      });
    }
  }, [sketchViewModel]);

  const _InitializeSketch = () => {
    const tmpSketchViewModel = new SketchViewModel({
      view: store.MapView,
      layer: graphicsLayer,
      pointSymbol: pointSymbol,
      polygonSymbol: polygonSymbol,
      polylineSymbol: lineSymbol,
      updateOnGraphicClick: false,
    });
    setSketchViewModel(tmpSketchViewModel);
  };
  const _DrawClickHandler = (graphicType: any) => {
    if (store.MapView && graphicType && sketchViewModel) {
      if (graphicType == "text") {
        sketchViewModel.pointSymbol = textSymbol;
        sketchViewModel.create("point");
      } else {
        sketchViewModel.pointSymbol = pointSymbol;
        sketchViewModel.create(graphicType);
      }
      setLastActiveTool(graphicType);
    }
  };

  const _ClearClickHandler = () => {
    if (sketchViewModel) {
      sketchViewModel.cancel();
    }
    const layersToRemove = store.MapView?.map.layers.toArray().filter((x) => {
      return x.id === "general-drawing";
    });
    if (layersToRemove) store.MapView?.map.layers.removeMany(layersToRemove);
    graphicsLayer?.graphics.removeAll();
    setAddedGraphics([]);
  };
  const _GetStartPoint = (geometry: any, tool: string) => {
    switch (tool) {
      case "point":
        return new Point({
          x: geometry.x,
          y: geometry.y,
          spatialReference: geometry.spatialReference,
        });
        break;
      case "rectangle":
        return new Point({
          x: geometry.extent.xmax,
          y: geometry.extent.ymax,
          spatialReference: geometry.spatialReference,
        });
        break;
      case "circle":
        return new Point({
          x: geometry.rings[0][geometry.rings[0].length - 2][0],
          y: geometry.rings[0][geometry.rings[0].length - 2][1],
          spatialReference: geometry.spatialReference,
        });
        break;
      case "polygon":
        return new Point({
          x: geometry.rings[0][geometry.rings[0].length - 2][0],
          y: geometry.rings[0][geometry.rings[0].length - 2][1],
          spatialReference: geometry.spatialReference,
        });
        break;
      case "polyline":
        return new Point({
          x: geometry.paths[0][geometry.paths[0].length - 1][0],
          y: geometry.paths[0][geometry.paths[0].length - 1][1],
          spatialReference: geometry.spatialReference,
        });
        break;
    }
  };
  return (
    <>
      <BasePanel
        openModal={activePanel === "drawing"}
        onClose={handleClose}
        panelName={data.services.General[0].title}
        ml="1px"
      >
        <div className={styles["draw-container"]}>
          <SketchTools
            drawClickHandler={_DrawClickHandler}
            clearGraphicsHandler={_ClearClickHandler}
          />
        </div>
        {graphicsLayer &&
          graphicsLayer?.graphics.length > 0 &&
          !layerIsHidden &&
          addedGraphics[0] &&
          addedGraphics[0].coordinates &&
          addedGraphics
            .filter((x) => x.coordinates[0] > 0 && x.coordinates[1] > 0)
            .map((x) => (
              <TextPopup
                type={x.type}
                deleteHandler={_DeleteHandler}
                sizeChangeHandler={_SizeChangeHandler}
                size={x.size}
                selectedColor={x.color}
                colorChangeHandler={_ColorChangeHandler}
                isEditing={x.isEditing}
                id={x.id.toString()}
                text={x.text}
                txtChangeHandler={_TxtChangeHandler}
                setEditHandler={_SetEditHandler}
                coordinates={x.coordinates}
              />
            ))}
        {graphicsLayer &&
          graphicsLayer?.graphics.length > 0 &&
          !layerIsHidden &&
          addedGraphics[0] &&
          addedGraphics[0].coordinates &&
          drawingSymbolMeasurements &&
          addedGraphics
            .filter((x) => x.coordinates[0] > 0 && x.coordinates[1] > 0)
            .map((x) => {
              const measurementObject = drawingSymbolMeasurements.find(
                (obj) => obj.id === Number(x.id)
              );
              if (measurementObject) {
                return x.type === "rectangle" ? (
                  <React.Fragment key={measurementObject.id}>
                    <TextSymbol
                      mapView={store.MapView}
                      coordinates={measurementObject.centerPoint1}
                      text={measurementObject.results[0]}
                    />
                    <TextSymbol
                      mapView={store.MapView}
                      coordinates={measurementObject.centerPoint2}
                      text={measurementObject.results[1]}
                    />
                  </React.Fragment>
                ) : x.type === "circle" || x.type === "polyline" ? (
                  <TextSymbol
                    mapView={store.MapView}
                    coordinates={measurementObject.centerPoint1}
                    text={measurementObject.results[0]}
                  />
                ) : null;
              }
            })}
      </BasePanel>
    </>
  );
};

export default Widget;
